/**
 * HMAC-SHA256 signature for the `x-client-signature` header.
 *
 * Mirrors the Go implementation used by every admin-community-api Lambda
 * (see admin-community-api-serverless/internal/gatewayauth/auth.go):
 *   hex(HMAC-SHA256(SECRET_KEY, x-client-id + ":" + x-auth-timestamp))
 *
 * SECURITY: SECRET_KEY ships inside the JS bundle. This module is therefore
 * *test-only* — using production secrets here exposes them to anyone loading
 * the page.
 */

function bytesToHex(bytes) {
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

export async function signRequest(secretKey, clientId, timestamp) {
  if (!secretKey) throw new Error('signRequest: secretKey is empty');
  if (!clientId) throw new Error('signRequest: clientId is empty');
  if (!timestamp) throw new Error('signRequest: timestamp is empty');

  const subtle = (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) || null;
  if (!subtle) {
    throw new Error('signRequest: Web Crypto subtle API is unavailable in this runtime');
  }
  const enc = new TextEncoder();
  const key = await subtle.importKey(
    'raw',
    enc.encode(secretKey),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await subtle.sign('HMAC', key, enc.encode(`${clientId}:${timestamp}`));
  return bytesToHex(new Uint8Array(sig));
}

/**
 * Convenience wrapper that returns the three required HMAC headers for the given clientId.
 */
export async function buildHmacHeaders(secretKey, clientId) {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const signature = await signRequest(secretKey, clientId, timestamp);
  return {
    'x-client-id': clientId,
    'x-auth-timestamp': timestamp,
    'x-client-signature': signature,
  };
}
